import React from 'react';
import AnimatedSection from './AnimatedSection';
import StaggeredList from './StaggeredList';
import { Shield, CheckCircle } from 'lucide-react';

const RegulatoryComplianceSection = () => {
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <AnimatedSection animation="fade-up">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6" style={{ color: '#214140' }}>
                  Supporting Patient Safety Initiatives
                </h2>
                <p className="text-xl text-gray-600 mb-6">
                  Education alone isn't enough. CMS, The Joint Commission, NIOSH, and AHRQ all emphasize environmental interventions for patient safety.
                </p>
                <StaggeredList delay={120} className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 flex-shrink-0 text-green-600 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">CMS Serious Reportable Events</p>
                      <p className="text-gray-600">Falls with major injury affect reimbursement and public reporting</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 flex-shrink-0 text-green-600 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Joint Commission Alert 55</p>
                      <p className="text-gray-600">Identifies tethering to medical equipment as a key fall contributor</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 flex-shrink-0 text-green-600 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">NIOSH/CDC Guidelines</p>
                      <p className="text-gray-600">Lists loose cords and medical tubing as top tripping hazards</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 flex-shrink-0 text-green-600 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">AHRQ Multi-Level Intervention</p>
                      <p className="text-gray-600">Calls for environmental modifications, not just staff education</p>
                    </div>
                  </div>
                </StaggeredList>
              </div>

              <div className="rounded-xl overflow-hidden shadow-xl">
                <img
                  src="/images/nurses-patients/young-nurse1-smiling.png"
                  alt="Professional nurse smiling confidently"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="rounded-2xl p-8 shadow-lg mt-12" style={{ backgroundColor: '#DFEAF0' }}>
              <h3 className="text-2xl font-bold mb-6 text-center" style={{ color: '#214140' }}>
                How Cordflex Supports These Initiatives
              </h3>
              <StaggeredList delay={100} className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 flex-shrink-0 text-green-600 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">Environmental Risk Reduction</p>
                    <p className="text-gray-600 text-sm">Designed to help keep lines and cords off the floor</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 flex-shrink-0 text-green-600 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">IV Line Tracing Support</p>
                    <p className="text-gray-600 text-sm">Designed to make IV line tracing easier. In a simulated ICU, line labels plus organizers cut infusion identification time by 61% (Pinkney et al., 2019).</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 flex-shrink-0 text-green-600 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">Fall Hazard Mitigation</p>
                    <p className="text-gray-600 text-sm">Designed to help manage lines during patient ambulation</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 flex-shrink-0 text-green-600 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">Root Cause Analysis Support</p>
                    <p className="text-gray-600 text-sm">A documented environmental control you can cite in fall and line-event action plans</p>
                  </div>
                </div>
              </StaggeredList>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default RegulatoryComplianceSection;
